import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Code, Smartphone, Layers, Cloud, TrendingUp, Cpu, Shield, Megaphone, Search, Server, Palette, Video } from 'lucide-react';
import type { Service } from '@/types';
import styles from './ServiceCard.module.scss';

interface ServiceCardProps {
  service: Service;
  index: number;
  delay?: number;
}

const iconMap: Record<string, React.ReactNode> = {
  code: <Code size={20} strokeWidth={1.5} />,
  smartphone: <Smartphone size={20} strokeWidth={1.5} />,
  layers: <Layers size={20} strokeWidth={1.5} />,
  cloud: <Cloud size={20} strokeWidth={1.5} />,
  'trending-up': <TrendingUp size={20} strokeWidth={1.5} />,
  cpu: <Cpu size={20} strokeWidth={1.5} />,
  shield: <Shield size={20} strokeWidth={1.5} />,
  megaphone: <Megaphone size={20} strokeWidth={1.5} />,
  search: <Search size={20} strokeWidth={1.5} />,
  server: <Server size={20} strokeWidth={1.5} />,
  palette: <Palette size={20} strokeWidth={1.5} />,
  video: <Video size={20} strokeWidth={1.5} />,
};

const ServiceCard: React.FC<ServiceCardProps> = ({ service, index, delay = 0 }) => {
  const formattedNumber = String(index + 1).padStart(2, '0');

  return (
    <motion.div
      className={`${styles.cardWrapper} ${styles[`cardVariant_${index % 6}`]}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link to={`/services#${service.id}`} className={styles.card} id={`service-card-${service.id}`}>
        
        {/* Top area */}
        <div className={styles.top}>
          <span className={styles.number}>{formattedNumber}</span>
          <div className={styles.iconWrapper}>
            {iconMap[service.icon]}
          </div>
        </div>

        {/* Dynamic accent line location */}
        <div className={styles.accentLine} />

        {/* Content */}
        <div className={styles.content}>
          <h3 className={styles.title}>{service.title}</h3>
          <p className={styles.description}>{service.description}</p>
          
          <div className={styles.capabilities}>
            {service.features.map((feature, i) => (
              <React.Fragment key={feature}>
                <span className={styles.capability}>{feature}</span>
                {i < service.features.length - 1 && <span className={styles.dot}>•</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div className={styles.bottom}>
          <div className={styles.arrow}>
            <ArrowRight size={20} strokeWidth={1.5} />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ServiceCard;
